import { AvailableFeedbackSteps, FeedbackWidgetStepConfig } from 'components/surveys/generic-feedback-widget/types.ts'
import { Checkbox, CheckboxList, Radio, RadioList, TextArea, TextField, Rating } from '@boltenergy-be/design-system'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styles from 'components/surveys/generic-feedback-widget/GenericFeedbackWidget.module.scss'
import classNames from 'classnames'
import { EXTRA_INPUT_KEY } from 'components/surveys/generic-feedback-widget/constants.ts'

const FeedbackWidgetFormField = <S extends AvailableFeedbackSteps>({
  type,
  question,
  options,
  maxOptions,
  optional,
  placeholder,
  step,
  active,
  range
}: FeedbackWidgetStepConfig & { step: S; active: boolean }) => {
  // i18n
  const { t } = useTranslation(['validation', 'feedbackWidget', 'common'])

  // React Hook Form
  const { register, formState, control, watch } = useFormContext()
  const watchCurrent = watch(step)

  // Constants
  const stepError = typeof formState?.errors?.[step]?.message === 'string' ? formState?.errors?.[step]?.message : undefined
  const hideClassName = classNames({ [styles.hidden]: !active })
  const optionWithExtraInputTrigger = options ? options.find((option) => option.triggerExtraInput) : undefined
  const shouldShowExtraInput: boolean = optionWithExtraInputTrigger
    ? Array.isArray(watchCurrent)
      ? watchCurrent.includes(optionWithExtraInputTrigger.value)
      : watchCurrent === optionWithExtraInputTrigger.value
    : false

  switch (type) {
    case 'radio': {
      return (
        <RadioList label={t(question)} error={stepError} grid="full" className={hideClassName}>
          {options.map((option) => (
            <Radio
              {...register(step, { required: !optional ? t(($) => $.required) : undefined })}
              id={`${step}-${option.value}`}
              value={option.value}
              key={`${step}-${option.value}`}
              label={t(option.label)}
              name={step}
            />
          ))}
        </RadioList>
      )
    }

    case 'checkbox': {
      return (
        <>
          <CheckboxList
            label={t(question)}
            error={stepError}
            grid="full"
            className={hideClassName}
            description={
              maxOptions
                ? t(($) => $.maxOptions, {
                    max: maxOptions,
                    ns: 'feedbackWidget'
                  })
                : undefined
            }
          >
            {options.map((option) => (
              <Checkbox
                {...register(step, {
                  required: !optional ? t(($) => $.required) : undefined,
                  validate: {
                    maxOptions: (value: string[]) =>
                      maxOptions
                        ? value?.length < maxOptions + 1 ||
                          t(($) => $.maxOptions, {
                            max: maxOptions
                          })
                        : false
                  }
                })}
                id={`${step}-${option.value}`}
                value={option.value}
                key={`${step}-${option.value}`}
                label={t(option.label)}
                name={step}
                disabled={maxOptions ? watchCurrent?.length >= maxOptions && !watchCurrent?.includes(option.value) : false}
              />
            ))}
          </CheckboxList>

          {optionWithExtraInputTrigger && shouldShowExtraInput && (
            <TextField
              key={`extra-input-${step}`}
              id={`extra-input-${step}`}
              grid="full"
              hiddenLabel
              placeholder={
                optionWithExtraInputTrigger.extraInputPlaceholder ? t(optionWithExtraInputTrigger.extraInputPlaceholder) : undefined
              }
              error={(formState?.errors?.[EXTRA_INPUT_KEY] as any)?.[step]?.message}
              label={optionWithExtraInputTrigger.extraInputLabel ? t(optionWithExtraInputTrigger.extraInputLabel) : ''}
              {...register(`${EXTRA_INPUT_KEY}.${step}`, {
                required: !optional ? t(($) => $.required) : undefined,
                maxLength: {
                  value: 255,
                  message: t(($) => $.invalid.length, {
                    length: 255
                  })
                }
              })}
            />
          )}
        </>
      )
    }

    case 'textArea': {
      return (
        <TextArea
          placeholder={t(placeholder)}
          label={t(question)}
          error={stepError}
          grid="full"
          layoutClassName={hideClassName}
          description={t(($) => $.maxCharacters, {
            max: 255,
            ns: 'feedbackWidget'
          })}
          {...register(step, {
            required: !optional ? t(($) => $.required) : undefined,
            maxLength: {
              value: 255,
              message: t(($) => $.invalid.length, {
                length: 255
              })
            }
          })}
        />
      )
    }

    case 'rating': {
      return (
        <Controller
          {...{ control }}
          name={step}
          rules={{ required: !optional ? t(($) => $.required) : undefined }}
          render={({ field: { onChange, ...register } }) => (
            <Rating
              {...register}
              limit={range.limit}
              onClick={(v) => onChange(v)}
              label={t(question)}
              minScoreLabel={t(range.minScoreLabel)}
              maxScoreLabel={t(range.maxScoreLabel)}
              error={stepError}
              value={watchCurrent}
            />
          )}
        />
      )
    }

    default: {
      return null
    }
  }
}

export default FeedbackWidgetFormField
